/**
 * Attach the signed-in user's Cognito ID token to campaign-service requests.
 *
 * Replaces the shared x-campaign-token secret. That token shipped inside the
 * public JS bundle, so it was extractable by anyone who read it and identified
 * nobody - every request looked identical. A per-user JWT is verifiable
 * server-side, expires on its own, is revoked by disabling the user, and names
 * who started a campaign or suppressed a contact.
 *
 * Installed as a global fetch wrapper rather than threading a header through
 * every call site: the components make dozens of fetch calls across several
 * files, and one missed call would be a silent 403 in production. This cannot
 * miss one.
 *
 * Only requests to the campaign service host get the header. Everything else -
 * other APIs, static assets, third parties - passes through untouched, so the
 * token is never leaked to an unrelated origin.
 */

import { getIdToken } from './auth';

const CAMPAIGN_HOSTS = [
  'xijjve6puc6ht4inqmmgbss4ay0earbn.lambda-url.us-east-1.on.aws'
];

function isCampaignRequest(input: RequestInfo | URL): boolean {
  try {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    if (!url) return false;
    return CAMPAIGN_HOSTS.some((host) => url.includes(host));
  } catch {
    return false;
  }
}

export function installCampaignAuth(): void {
  if (typeof window === 'undefined' || !window.fetch) return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isCampaignRequest(input)) return originalFetch(input, init);

    const token = await getIdToken();
    const headers = new Headers(init?.headers || (input as Request)?.headers || undefined);

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      // Let it go and 401 rather than failing invisibly. AuthGuard should
      // already have redirected to sign-in before this happens.
      console.warn('[campaign-auth] No valid session; request will be rejected.');
    }

    return originalFetch(input, { ...(init || {}), headers });
  };
}
